import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { TableRow, TableCell, Stack, Typography, Chip, Tooltip } from '@mui/material';
import AccountAutocomplete from './AccountAutocomplete';

function DocRow({ clientId, doc, rowKey, isSubRow, ligneIndex, totalLignes, onSaved, onAccountsChange }) {
    const [debit, setDebit] = useState('');
    const [credit, setCredit] = useState('');
    const [auto, setAuto] = useState(false);
    const reqId = useRef(0);

    // Charger les regles comptables pour le document (ou la ligne)
    useEffect(() => {
        let canceled = false;
        const ctrl = new AbortController();
        const current = ++reqId.current;

        (async () => {
            try {
                const { data } = await axios.get('/api/comptable/accounting/suggest', {
                    params: {
                        clientId,
                        docId: doc._id,
                        ligneIndex: isSubRow ? ligneIndex : undefined,
                        includeLabels: 1,
                    },
                    signal: ctrl.signal,
                });

                if (canceled || current !== reqId.current) return;

                const sug = data?.suggestion;
                if (sug) {
                    setDebit(prev => prev || String(sug.debit ?? ''));
                    setCredit(prev => prev || String(sug.credit ?? ''));
                    setAuto(true);
                } else {
                    setAuto(false);
                }
            } catch {
                // silencieux
            }
        })();

        return () => {
            canceled = true;
            ctrl.abort();
        };
    }, [clientId, doc?._id, isSubRow, ligneIndex]);

    // Remonter en continu au parent
    useEffect(() => {
        onAccountsChange?.(rowKey || doc._id, {
            debit: debit || null,
            credit: credit || null,
        });
    }, [debit, credit, rowKey, doc._id, onAccountsChange]);

    const name    = doc?.contrepartie?.nomOuRaison || doc?.libelle || '\u2014';
    const mf      = doc?.contrepartie?.matriculeFiscal || '';
    const nature  = doc?.retenue?.natureOperation || '';
    const taux    = doc?.retenue?.tauxRetenue || '';
    const base    = doc?.retenue?.baseHT || '';
    const tva     = doc?.retenue?.tvaDue || '';
    const total   = doc?.retenue?.totalTTC || '';

    // Tronquer la nature pour l'affichage
    const natureShort = nature.length > 60 ? nature.slice(0, 57) + '...' : nature;

    return (
        <TableRow
            hover
            sx={isSubRow ? { bgcolor: 'grey.50' } : {}}
        >
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Stack spacing={0.25}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {name}
                        {isSubRow && (
                            <Chip
                                size="small"
                                label={`${ligneIndex + 1}/${totalLignes}`}
                                sx={{ ml: 1, fontSize: '0.65rem', height: 18 }}
                                variant="outlined"
                            />
                        )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{mf || '\u2014'}</Typography>
                </Stack>
            </TableCell>

            <TableCell align="right">
                <Stack spacing={0.25} alignItems="flex-end">
                    <Typography variant="body2">{base}</Typography>
                    <Typography variant="caption" color="text.secondary">TVA {tva} - TTC {total}</Typography>
                </Stack>
            </TableCell>

            <TableCell align="center">
                {taux ? <Chip size="small" label={`RS ${taux}%`} /> : <Chip size="small" variant="outlined" label={'\u2014'} />}
            </TableCell>

            <TableCell>
                <AccountAutocomplete
                    clientId={clientId}
                    value={debit}
                    onChange={(v) => setDebit(String(v ?? ''))}
                    label="Debit"
                />
            </TableCell>

            <TableCell>
                <AccountAutocomplete
                    clientId={clientId}
                    value={credit}
                    onChange={(v) => setCredit(String(v ?? ''))}
                    label="Credit"
                />
            </TableCell>

            <TableCell>
                <Tooltip title={doc.isIntegrated ? 'Ce document a ete integre dans un export Excel.' : 'Ce document n\'a pas encore ete integre.'}>
                    <Typography variant="body2">{doc.isIntegrated ? 'Oui' : 'Non'}</Typography>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
}

export default React.memo(DocRow, (prev, next) =>
    prev.clientId === next.clientId &&
    prev.rowKey === next.rowKey &&
    prev.doc._id === next.doc._id &&
    prev.doc.isIntegrated === next.doc.isIntegrated &&
    prev.ligneIndex === next.ligneIndex
);
